import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Modal,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  AppBar,
  Toolbar,
  Fade,
  Pagination,
  Badge,
  CircularProgress,
  styled,
  useTheme,
  Backdrop,
  useMediaQuery,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CalendarToday,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import LeftToolbar from "./LeftToolbar";
import { alpha } from "@mui/material/styles";
import Footer from "./Footer";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";
const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fa",
});

const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: $collapsed ? "80px" : "280px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
  overflowX: "auto",
  background: "white",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&::-webkit-scrollbar": {
    height: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    borderRadius: "3px",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "12px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: alpha(theme.palette.primary.light, 0.03),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.08),
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[1],
  },
  transition: theme.transitions.create(["background-color", "transform"], {
    duration: theme.transitions.duration.shortest,
  }),
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));
const ModernTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&:first-of-type": {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));
const RespondedTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState("Pending Tickets");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [error, setError] = useState(null);
  const ticketsPerPage = 5;
  const [roleFilter, setRoleFilter] = useState("");
  const [openLogsDialog, setOpenLogsDialog] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [ticketLogs, setTicketLogs] = useState([]);
  const [logsError, setLogsError] = useState(null);

  const API_BASE_URL = "/api/support";
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState({
    mode: "none",
    singleDate: "",
    startDate: "",
    endDate: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "CreatedOn",
    direction: "asc",
  });
  const [user, setUser] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      return userData ? JSON.parse(userData) : {};
    } catch (error) {
      console.error("Error parsing user data:", error);
      return {};
    }
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  useEffect(() => {
    const fetchTickets = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axios.get(`${API_BASE_URL}/respondedTickets`, {
          params: {
            page: currentPage,
            pageSize: ticketsPerPage,
            search: searchTerm,
          },
          withCredentials: true,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });
        setTickets(Array.isArray(data.tickets) ? data.tickets : []);
        setTotalTickets(data.total || 0);
      } catch (error) {
        setError(
          error.response?.data?.message ||
            error.message ||
            "Failed to fetch tickets",
        );
        setTickets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [currentPage, searchTerm]);
  const uniqueCategories = [
    ...new Set(
      Array.isArray(tickets)
        ? tickets.map((ticket) => ticket.categoryName)
        : [],
    ),
  ];
  const highlightMatch = (text, term) => {
    if (!term || !text) return text;
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedTerm})`, "gi");
    return text
      .toString()
      .split(regex)
      .map((part, i) =>
        regex.test(part) ? <mark key={i}>{part}</mark> : part,
      );
  };

  const sortTickets = (tickets) => {
    if (!sortConfig.key) return tickets;
    return [...tickets].sort((a, b) => {
      if (sortConfig.key === "CreatedOn") {
        const dateA = new Date(a.CreatedOn).getTime();
        const dateB = new Date(b.CreatedOn).getTime();
        if (dateA < dateB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (dateA > dateB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      }
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };
  const filteredTickets = Array.isArray(tickets)
    ? sortTickets(
        tickets.filter((ticket) => {
          const matchesSearch =
            ticket.ticketDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ticket.Sender?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ticket.InstitutionName?.toLowerCase().includes(searchTerm.toLowerCase());
          const matchesCategory =
            selectedCategory === "" || ticket.categoryName === selectedCategory;
          let matchesDate = true;
          if (dateFilter.mode === "single" && dateFilter.singleDate) {
            const ticketDate = new Date(ticket.createdOn)
              .toISOString()
              .split("T")[0];
            matchesDate = ticketDate === dateFilter.singleDate;
          } else if (dateFilter.mode === "range") {
            const ticketDate = new Date(ticket.createdOn).getTime();
            const startDate = dateFilter.startDate
              ? new Date(dateFilter.startDate).getTime()
              : 0;
            const endDate = dateFilter.endDate
              ? new Date(dateFilter.endDate).getTime() + 86400000
              : Infinity;
            matchesDate = ticketDate >= startDate && ticketDate < endDate;
          }
          const matchesRole =
            roleFilter === "" || ticket.UserRoleId === roleFilter;
          return matchesSearch && matchesCategory && matchesDate && matchesRole;
        }),
      )
    : [];
  const currentTickets = filteredTickets;
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      };

      return date.toLocaleString("en-GB", options);
    } catch {
      return dateString;
    }
  };
  useEffect(() => {
    if (
      dateFilter.mode === "range" &&
      dateFilter.startDate &&
      dateFilter.endDate
    ) {
      // const start = new Date(dateFilter.startDate);
      // const end = new Date(dateFilter.endDate);
    }
  }, [dateFilter.mode, dateFilter.startDate, dateFilter.endDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchTicketLogs = (ticketId) => {
    setLogsLoading(true);
    setLogsError(null);
    setTicketLogs([]);

    const urls = [
      `${API_BASE_URL}/ticketLogs/${ticketId}`,
      `${API_BASE_URL}/ticket/${ticketId}/logs`,
      `${API_BASE_URL}/ticketLogs?ticketId=${ticketId}`,
    ];

    const tryFetchLogs = (index) => {
      if (index >= urls.length) {
        setLogsError("No logs available for this ticket");
        setLogsLoading(false);
        return;
      }

      fetch(urls[index], {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
        .then((res) => {
          if (!res.ok) throw new Error(`Status ${res.status}`);
          return res.json();
        })
        .then((data) => {
          const logs = Array.isArray(data) ? data : data?.logs || [];
          setTicketLogs(logs);
          setLogsLoading(false);
        })
        .catch(() => tryFetchLogs(index + 1));
    };

    tryFetchLogs(0);
  };
  const safeValue = (value) =>
    value !== null && value !== undefined && value !== "" ? value : "N/A";

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };
  const handleViewLogs = (ticket, event) => {
    event?.preventDefault();
    event?.stopPropagation();
    setSelectedTicket(ticket);
    const ticketId = ticket?.TicketId || ticket?.id || ticket?._id;

    if (ticketId) {
      fetchTicketLogs(ticketId);
      setOpenLogsDialog(true);
    } else {
      setError("Cannot view logs: Invalid ticket ID");
    }
  };
  const handleCloseLogsDialog = () => {
    setOpenLogsDialog(false);
  };
  const hasAssignmentData = (log) => {
    return (
      (log?.AssignedDate &&
        log.AssignedDate !== "N/A" &&
        log.AssignedDate !== "") ||
      (log?.AssignedBy && log.AssignedBy !== "N/A" && log.AssignedBy !== "") ||
      (log?.AssignedTo && log.AssignedTo !== "N/A" && log.AssignedTo !== "") ||
      (log?.AssignementReason &&
        log.AssignementReason !== "N/A" &&
        log.AssignementReason !== "")
    );
  };
  return (
    <MainContainer>
      <AppBar
        position="fixed"
        sx={{
          backgroundImage: "linear-gradient(195deg, #0a2e52 0%, #1565C0 200%)",
          boxShadow:
            "0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 0, 0, 0.4)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          left: collapsed ? "80px" : "280px",
          width: `calc(100% - ${collapsed ? "80px" : "280px"})`,
          transition: theme.transitions.create(["width", "left"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          [theme.breakpoints.down("sm")]: {
            left: 0,
            width: "100%",
          },
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
            IPPIS SUPPORT PORTAL
          </Typography>

          <IconButton color="inherit">
            <Badge
              badgeContent={
                filteredTickets.length > 999 ? "999+" : filteredTickets.length
              }
              color="error"
              max={9999}
              onClick={() => {
                if (user.roleId === "ADMIN" || user.roleId === "SUPPORT") {
                  navigate("/Dashboard");
                }
              }}
              sx={{
                cursor:
                  user.roleId === "ADMIN" || user.roleId === "SUPPORT"
                    ? "pointer"
                    : "not-allowed",
                opacity: user.roleId === "ADMIN" || user.roleId === "SUPPORT",
              }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <LeftToolbar
        selectedMenu={selectedMenu}
        onSelectMenu={setSelectedMenu}
        onCollapse={setCollapsed}
      />

      <MainContent $collapsed={collapsed}>
        <Toolbar />

        {/* Active filters display */}
        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {selectedCategory && (
            <Chip
              label={`Module: ${selectedCategory}`}
              onDelete={() => setSelectedCategory("")}
              sx={{ borderRadius: "12px" }}
            />
          )}

          {roleFilter && (
            <Chip
              label={`Role: ${roleFilter}`}
              onDelete={() => setRoleFilter("")}
              sx={{ borderRadius: "12px" }}
            />
          )}

          {dateFilter.mode === "single" && dateFilter.singleDate && (
            <Chip
              label={`Date: ${new Date(
                dateFilter.singleDate,
              ).toLocaleDateString()}`}
              onDelete={() => setDateFilter({ ...dateFilter, singleDate: "" })}
              sx={{ borderRadius: "12px" }}
            />
          )}
          {dateFilter.mode === "range" &&
            (dateFilter.startDate || dateFilter.endDate) && (
              <Chip
                label={`Range: ${
                  dateFilter.startDate
                    ? new Date(dateFilter.startDate).toLocaleDateString()
                    : "..."
                } to ${
                  dateFilter.endDate
                    ? new Date(dateFilter.endDate).toLocaleDateString()
                    : "..."
                }`}
                onDelete={() =>
                  setDateFilter({ mode: "range", startDate: "", endDate: "" })
                }
                sx={{ borderRadius: "12px" }}
              />
            )}
        </Box>

        {/* Search and filter controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <TextField
            label="Search tickets..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: isMobile ? "100%" : 400,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{ display: "flex", gap: 2, width: isMobile ? "100%" : "auto" }}
          >
            {/* Date filter controls */}
            <Select
              value={dateFilter.mode}
              onChange={(e) =>
                setDateFilter({ ...dateFilter, mode: e.target.value })
              }
              size="small"
              sx={{ minWidth: 120, borderRadius: "12px" }}
            >
              <MenuItem value="none">No date filter</MenuItem>
              <MenuItem value="single">Specific date</MenuItem>
              <MenuItem value="range">Date range</MenuItem>
            </Select>

            {dateFilter.mode === "single" && (
              <TextField
                type="date"
                size="small"
                value={dateFilter.singleDate}
                onChange={(e) =>
                  setDateFilter({ ...dateFilter, singleDate: e.target.value })
                }
                sx={{ borderRadius: "12px" }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {dateFilter.mode === "range" && (
              <>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={dateFilter.startDate}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, startDate: e.target.value })
                  }
                  sx={{ borderRadius: "12px" }}
                  InputLabelProps={{ shrink: true }}
                />
                <Typography sx={{ alignSelf: "center" }}>to</Typography>
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={dateFilter.endDate}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, endDate: e.target.value })
                  }
                  sx={{ borderRadius: "12px" }}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}

            {/* Quick date range presets */}
            {dateFilter.mode === "range" && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = new Date();
                    const yesterday = new Date(today);
                    yesterday.setDate(yesterday.getDate() - 1);

                    setDateFilter({
                      ...dateFilter,
                      startDate: yesterday.toISOString().split("T")[0],
                      endDate: today.toISOString().split("T")[0],
                    });
                  }}
                  sx={{ borderRadius: "12px" }}
                >
                  Yesterday
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = new Date();
                    const lastWeek = new Date(today);
                    lastWeek.setDate(lastWeek.getDate() - 7);

                    setDateFilter({
                      ...dateFilter,
                      startDate: lastWeek.toISOString().split("T")[0],
                      endDate: today.toISOString().split("T")[0],
                    });
                  }}
                  sx={{ borderRadius: "12px" }}
                >
                  Last 7 days
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = new Date();
                    const lastMonth = new Date(today);
                    lastMonth.setMonth(lastMonth.getMonth() - 1);

                    setDateFilter({
                      ...dateFilter,
                      startDate: lastMonth.toISOString().split("T")[0],
                      endDate: today.toISOString().split("T")[0],
                    });
                  }}
                  sx={{ borderRadius: "12px" }}
                >
                  Last 30 days
                </Button>
              </Box>
            )}

            {/* Clear date filters button */}
            {(dateFilter.mode === "single" || dateFilter.mode === "range") && (
              <Button
                variant="outlined"
                onClick={() =>
                  setDateFilter({
                    mode: "none",
                    singleDate: "",
                    startDate: "",
                    endDate: "",
                  })
                }
                sx={{ borderRadius: "12px" }}
              >
                Clear
              </Button>
            )}

            {/* Role filter */}
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            >
              <MenuItem value="">All Roles</MenuItem>
              {Array.isArray(tickets) &&
                [...new Set(tickets.map((ticket) => ticket.UserRoleId))].map(
                  (role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ),
                )}
            </Select>

            {/* Category filter */}
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 200,
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
              startAdornment={
                <InputAdornment position="start">
                  <FilterIcon fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="">All Modules</MenuItem>
              {uniqueCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Tickets table */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            p: 3,
            mb: 3,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Responded Tickets
            </Typography>
            {!["ADMIN", "SUPPORT"].includes(user?.roleId) && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/CreateTicket")}
                startIcon={<AddIcon />}
                sx={{
                  fontWeight: 600,
                  borderRadius: "999px",
                  textTransform: "none",
                  bgcolor: theme.palette.primary.light,
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                  },
                }}
              >
                Request new support
              </Button>
            )}
            <Typography variant="body2" color="text.secondary">
              {totalTickets} tickets found
            </Typography>
          </Box>

          <ResponsiveTableContainer component={Paper}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: theme.palette.primary.light,
                    "& th": {
                      color: "white",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    },
                  }}
                >
                  {[
                    { id: "id", label: "Id" },
                    { id: "TicketDescription", label: "Description" },
                    { id: "Sender", label: "Sender" },
                    { id: "InstitutionName", label: "Institution" },
                    { id: "categoryName", label: "Module" },
                    { id: "UserRoleId", label: "Role" },
                    { id: "CreatedOn", label: "Date" },
                    { id: "Ticket Logs", label: "Ticket Logs" },
                  ].map((column) => (
                    <ModernTableCell
                      key={column.id}
                      onClick={() => {
                        if (column.id !== "actions") {
                          let direction = "desc";
                          if (sortConfig.key === column.id) {
                            direction =
                              sortConfig.direction === "desc" ? "asc" : "desc";
                          }
                          setSortConfig({ key: column.id, direction });
                        }
                      }}
                      sx={{
                        cursor: column.id !== "actions" ? "pointer" : "default",
                        "&:hover": {
                          backgroundColor:
                            column.id !== "actions"
                              ? alpha(theme.palette.primary.main, 0.2)
                              : "inherit",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        {column.label}
                        {sortConfig.key === column.id && (
                          <Box ml={1} display="flex" flexDirection="column">
                            <span
                              style={{
                                lineHeight: 0.5,
                                color:
                                  sortConfig.direction === "asc"
                                    ? "#fff"
                                    : alpha("#fff", 0.5),
                                fontSize: "0.7rem",
                              }}
                            >
                              ▲
                            </span>
                            <span
                              style={{
                                lineHeight: 0.5,
                                color:
                                  sortConfig.direction === "desc"
                                    ? "#fff"
                                    : alpha("#fff", 0.5),
                                fontSize: "0.7rem",
                              }}
                            >
                              ▼
                            </span>
                          </Box>
                        )}
                      </Box>
                    </ModernTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <ModernTableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Loading tickets...
                      </Typography>
                    </ModernTableCell>
                  </TableRow>
                ) : currentTickets.length > 0 ? (
                  currentTickets.map((ticket) => (
                    <StyledTableRow key={ticket.TicketId} hover>
                      <ModernTableCell>{ticket.TicketId}</ModernTableCell>
                      <ModernTableCell>
                        {isMobile
                          ? highlightMatch(
                              (ticket.ticketDescription || "").substring(0, 20),
                              searchTerm,
                            )
                          : (ticket.ticketDescription || "").length > 30
                            ? highlightMatch(
                                (ticket.ticketDescription || "").substring(
                                  0,
                                  27,
                                ),
                                searchTerm,
                              )
                            : highlightMatch(
                                ticket.TicketDescription || "",
                                searchTerm,
                              )}
                      </ModernTableCell>

                      <ModernTableCell>
                        {highlightMatch(ticket.Sender, searchTerm)}
                      </ModernTableCell>
                      <ModernTableCell>
                        {highlightMatch(ticket.InstitutionName, searchTerm)}
                      </ModernTableCell>

                      <ModernTableCell>{ticket.categoryName}</ModernTableCell>
                      <ModernTableCell>{ticket.UserRoleId}</ModernTableCell>
                      <ModernTableCell>
                        <Tooltip title={formatDateForDisplay(ticket.CreatedOn)}>
                          <Box>
                            <Box
                              fontWeight={
                                sortConfig.key === "CreatedOn" ? 600 : 400
                              }
                            >
                              {formatDateForDisplay(ticket.createdOn)}
                            </Box>
                            <Box fontSize="0.75rem" color="text.secondary">
                              {formatDateForDisplay(ticket.createdOn)}
                            </Box>
                          </Box>
                        </Tooltip>
                      </ModernTableCell>
                      <ModernTableCell>
                        <IconButton
                          color="primary"
                          onClick={(e) => handleViewLogs(ticket, e)}
                          title="View Ticket Logs"
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.1,
                              ),
                            },
                          }}
                        >
                          <VisibilityIcon
                            fontSize={isMobile ? "small" : "medium"}
                          />
                        </IconButton>
                      </ModernTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <ModernTableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1">No tickets found</Typography>
                    </ModernTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ResponsiveTableContainer>

          {!loading && filteredTickets.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={Math.ceil(totalTickets / ticketsPerPage)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </Box>
      </MainContent>

      {/* Modified Modal for Ticket Logs */}
      <Modal
        open={openLogsDialog}
        onClose={handleCloseLogsDialog}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      >
        <Fade in={openLogsDialog}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              p: 3,
              borderRadius: 2,
              boxShadow: 24,
              width: { xs: "95%", sm: "80%" },
              maxWidth: "900px",
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight="bold"></Typography>
              <IconButton onClick={handleCloseLogsDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Approved Status Bar */}
            <Box
              sx={{
                bgcolor: theme.palette.primary.light,
                color: "white",
                p: 2,
                borderRadius: "4px 4px 0 0",
              }}
            >
              Ticket #
              {selectedTicket?.TicketId ||
                selectedTicket?.id ||
                selectedTicket?._id}
            </Box>

            {logsLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 4,
                }}
              >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading logs...</Typography>
              </Box>
            ) : logsError ? (
              <Box
                sx={{
                  p: 2,
                  textAlign: "center",
                }}
              >
                <Typography color="error" variant="h6" gutterBottom>
                  {logsError}
                </Typography>
              </Box>
            ) : (
              <>
                {ticketLogs.length > 0 ? (
                  <Box>
                    <Table sx={{ mb: 4, "& td": { py: 1 } }}>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            component="th"
                            sx={{
                              fontWeight: "bold",
                              verticalAlign: "top",
                              border: "none",
                            }}
                          >
                            Ticket Description
                          </TableCell>
                          <TableCell sx={{ border: "none" }}>
                            {safeValue(
                              ticketLogs[0]?.ticketDescription ||
                                ticketLogs[0]?.Reason,
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            component="th"
                            sx={{
                              fontWeight: "bold",
                              verticalAlign: "top",
                              border: "none",
                            }}
                          >
                            CreatedBy
                          </TableCell>
                          <TableCell sx={{ border: "none" }}>
                            {/* {safeValue(ticketLogs[0]?.Userid)} */}
                            {safeValue(ticketLogs[0]?.CreatedBy)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              verticalAlign: "top",
                              border: "none",
                            }}
                          >
                            Created Date
                          </TableCell>

                          <ModernTableCell>
                            {(() => {
                              const date = new Date(ticketLogs[0]?.CreatedDate);
                              const pad = (num) =>
                                num.toString().padStart(2, "0");

                              // Get UTC hours and convert to 12-hour format
                              let hours = date.getUTCHours();
                              const ampm = hours >= 12 ? "PM" : "AM";
                              hours = hours % 12;
                              hours = hours ? hours : 12; // Convert 0 to 12

                              return `${date.getUTCFullYear()}-${pad(
                                date.getUTCMonth() + 1,
                              )}-${pad(date.getUTCDate())} ${pad(hours)}:${pad(
                                date.getUTCMinutes(),
                              )} ${ampm}`;
                            })()}
                          </ModernTableCell>
                        </TableRow>

                        {/* Conditionally render assignment-related rows only if there's actual assignment data */}
                        {hasAssignmentData(ticketLogs[0]) && (
                          <>
                            {ticketLogs[0]?.AssignedDate &&
                              ticketLogs[0].AssignedDate !== "N/A" &&
                              ticketLogs[0].AssignedDate !== "" && (
                                <TableRow>
                                  <TableCell
                                    component="th"
                                    sx={{
                                      fontWeight: "bold",
                                      verticalAlign: "top",
                                      border: "none",
                                    }}
                                  >
                                    Assigned Date
                                  </TableCell>
                                  <TableCell sx={{ border: "none" }}>
                                    {formatDate(ticketLogs[0]?.AssignedDate)}
                                  </TableCell>
                                </TableRow>
                              )}
                            {ticketLogs[0]?.AssignedBy &&
                              ticketLogs[0].AssignedBy !== "N/A" &&
                              ticketLogs[0].AssignedBy !== "" && (
                                <TableRow>
                                  <TableCell
                                    component="th"
                                    sx={{
                                      fontWeight: "bold",
                                      verticalAlign: "top",
                                      border: "none",
                                    }}
                                  >
                                    Assigned By
                                  </TableCell>
                                  <TableCell sx={{ border: "none" }}>
                                    {ticketLogs[0]?.AssignedBy}
                                  </TableCell>
                                </TableRow>
                              )}
                            {ticketLogs[0]?.AssignedTo &&
                              ticketLogs[0].AssignedTo !== "N/A" &&
                              ticketLogs[0].AssignedTo !== "" && (
                                <TableRow>
                                  <TableCell
                                    component="th"
                                    sx={{
                                      fontWeight: "bold",
                                      verticalAlign: "top",
                                      border: "none",
                                    }}
                                  >
                                    Assigned To
                                  </TableCell>
                                  <TableCell sx={{ border: "none" }}>
                                    {ticketLogs[0]?.AssignedTo}
                                  </TableCell>
                                </TableRow>
                              )}
                            {ticketLogs[0]?.AssignementReason &&
                              ticketLogs[0].AssignementReason !== "N/A" &&
                              ticketLogs[0].AssignementReason !== "" && (
                                <TableRow>
                                  <TableCell
                                    component="th"
                                    sx={{
                                      fontWeight: "bold",
                                      verticalAlign: "top",
                                      border: "none",
                                    }}
                                  >
                                    Assigned Reason
                                  </TableCell>
                                  <TableCell sx={{ border: "none" }}>
                                    {ticketLogs[0]?.AssignementReason}
                                  </TableCell>
                                </TableRow>
                              )}
                          </>
                        )}

                        <TableRow>
                          <TableCell
                            component="th"
                            sx={{
                              fontWeight: "bold",
                              verticalAlign: "top",
                              border: "none",
                            }}
                          >
                            Respondedby
                          </TableCell>
                          <TableCell sx={{ border: "none" }}>
                            {safeValue(
                              user?.roleId &&
                                !["ADMIN", "SUPPORT"].includes(user.roleId)
                                ? "IPPIS TEAM"
                                : ticketLogs[0]?.RespondedBy,
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            component="th"
                            sx={{
                              fontWeight: "bold",
                              verticalAlign: "top",
                              border: "none",
                            }}
                          >
                            Response
                          </TableCell>
                          <TableCell sx={{ border: "none" }}>
                            {safeValue(ticketLogs[0]?.Response)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            // component="th"
                            sx={{
                              fontWeight: "bold",
                              verticalAlign: "top",
                              border: "none",
                            }}
                          >
                            Responded Date
                          </TableCell>

                          <ModernTableCell>
                            {(() => {
                              const date = new Date(
                                ticketLogs[0]?.RespondedDate,
                              );
                              const pad = (num) =>
                                num.toString().padStart(2, "0");

                              // Get UTC hours and convert to 12-hour format
                              let hours = date.getUTCHours();
                              const ampm = hours >= 12 ? "PM" : "AM";
                              hours = hours % 12;
                              hours = hours ? hours : 12; // Convert 0 to 12

                              return `${date.getUTCFullYear()}-${pad(
                                date.getUTCMonth() + 1,
                              )}-${pad(date.getUTCDate())} ${pad(hours)}:${pad(
                                date.getUTCMinutes(),
                              )} ${ampm}`;
                            })()}
                          </ModernTableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    <Box
                      sx={{
                        bgcolor: theme.palette.primary.light,
                        color: "white",
                        p: 2,
                        borderRadius: "4px 4px 0 0",
                      }}
                    >
                      <Typography variant="h6">
                        Supporting document(s)
                      </Typography>
                    </Box>
                    <TableContainer
                      component={Paper}
                      sx={{
                        mb: 2,
                        boxShadow: "none",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Table size="small">
                        <Typography sx={{ mb: 2 }}>
                          View Attachment:
                          {selectedTicket?.AttachmentRefPath ? (
                            <Button
                              href={`https://documents.ippis.rw/preview/${selectedTicket.AttachmentRefPath}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="text"
                              color="primary"
                              sx={{ textTransform: "none" }}
                              startIcon={<VisibilityIcon />}
                            ></Button>
                          ) : (
                            "No attachment available"
                          )}
                        </Typography>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 4,
                    }}
                  >
                    <Typography color="textSecondary">
                      No activity logs found for this ticket.
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Fade>
      </Modal>
      <Footer collapsed={collapsed} />
    </MainContainer>
  );
};

export default RespondedTickets;
